/**
 * Customer Success — Menu déroulant Actions rapides.
 *
 * Affiché depuis l'icône "⋮" sur une ligne client ou une carte d'action recommandée.
 * Chaque entrée ouvre la modale correspondante via useClientActions().
 */
import React from 'react';
import axios from 'axios';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from '../../ui/dropdown-menu';
import { Button } from '../../ui/button';
import {
  User, Phone, MessageCircle, Mail, ClipboardList, StickyNote,
  CheckCircle2, MoreHorizontal, Send,
} from 'lucide-react';
import { useClientActions } from './ClientActionsContext';
import { useToast } from '../../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClientActionsMenu({ client, align = 'end' }) {
  const { open } = useClientActions();
  const { toast } = useToast();

  if (!client) return null;

  const handleMarkProcessed = async () => {
    try {
      await axios.post(`${API}/admin/customer-success/actions`, {
        companyName: client.companyName,
        clientEmail: client.primaryEmail,
        action: 'marked_processed',
        visibility: 'cs',
      });
      toast({
        title: 'Action marquée comme traitée',
        description: `${client.companyName || 'Client'} — Suivi clôturé.`,
      });
    } catch (e) {
      toast({ title: 'Erreur', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          data-testid={`actions-menu-${client.companyName}`}
          aria-label="Actions rapides"
        >
          <MoreHorizontal className="w-3.5 h-3.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="w-56" data-testid="actions-menu-content">
        <DropdownMenuLabel className="text-[11px] text-gray-500 uppercase tracking-wide">
          Actions rapides
        </DropdownMenuLabel>
        <DropdownMenuItem onClick={() => open('view', client)} data-testid="menu-view-client">
          <User className="w-4 h-4 mr-2 text-gray-600" /> Voir le détail client
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => open('relance', client)} data-testid="menu-relance">
          <Send className="w-4 h-4 mr-2 text-emerald-600" /> Relancer le client
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => open('message', client)} data-testid="menu-message">
          <MessageCircle className="w-4 h-4 mr-2 text-emerald-600" /> Envoyer un message
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => open('call', client)} data-testid="menu-call">
          <Phone className="w-4 h-4 mr-2 text-emerald-600" /> Appeler le client
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => open('email', client)} data-testid="menu-email">
          <Mail className="w-4 h-4 mr-2 text-emerald-600" /> Envoyer un email
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => open('task', client)} data-testid="menu-task">
          <ClipboardList className="w-4 h-4 mr-2 text-gray-600" /> Créer une tâche de suivi
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => open('note', client)} data-testid="menu-note">
          <StickyNote className="w-4 h-4 mr-2 text-gray-600" /> Ajouter une note
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleMarkProcessed} data-testid="menu-mark-processed">
          <CheckCircle2 className="w-4 h-4 mr-2 text-emerald-600" /> Marquer comme traité
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
